// src/views/services/categories/ListServiceCategories.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Image,
  useToast,
  useDisclosure,
  Select,
  Badge,
  Switch,
  SimpleGrid,
  useBreakpointValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import { MdEdit, MdDone, MdCancel, MdAdd } from "react-icons/md";
import Card from "components/card/Card";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import InsertServiceCategory from "./InsertServiceCategory";

const ListServiceCategories = ({ serviceId, serviceName }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [serviceCategories, setServiceCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState({
    canList: true,
    canEdit: true,
    canDelete: true,
    canInsert: true,
  });
  const isMobile = useBreakpointValue({ base: true, md: false });

  const pageId = 13; // Page ID for service categories management

  // -------- fetch permissions (same like items) ----------
  useEffect(() => {
    try {
      const role = JSON.parse(sessionStorage.getItem("permissions") || "{}");
      if (!role.permissions) return;
      const permission = role.permissions.find((p) => p.page_id === pageId);
      if (permission) {
        setPermissions({
          canList: permission.read_all_access || false,
          canEdit: permission.update_access || false,
          canDelete: permission.delete_access || false,
          canInsert: permission.insert_access || false,
        });
      }
    } catch (error) {
      toast({
        title: t("Error loading permissions"),
        description: error.message || "Something went wrong while fetching permissions.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  }, [toast, t]);

  // -------- fetch categories by service -----------
  const fetchServiceCategories = async (id) => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/service-categories/show?service_id=${id}&page_id=${pageId}`;
      const data = await fetchWithToken(url, "GET");
      const rows = data.data || [];
      setServiceCategories(rows);
      setFilteredCategories(rows);
    } catch (error) {
      toast({
        title: t("Failed to fetch categories"),
        description: error.message || t("Could not load service categories"),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      setServiceCategories([]);
      setFilteredCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (serviceId) {
      fetchServiceCategories(serviceId);
    }
  }, [serviceId]);

  // ---------- search ----------
  const handleSearch = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    if (!Array.isArray(serviceCategories)) {
      setFilteredCategories([]);
      return;
    }
    const filtered = serviceCategories.filter((cat) =>
      (cat.name || "").toLowerCase().includes(q.toLowerCase()) ||
      (cat.translation_key || "").toLowerCase().includes(q.toLowerCase())
    );
    setFilteredCategories(filtered);
  };

  // ---------- image upload (edit mode) ----------
  const handleCategoryImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditingCategory((prev) => ({
        ...prev,
        image: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  // ---------- save edited category ----------
  const handleUpdateCategory = async () => {
    try {
      const { image, ...rest } = editingCategory;
      const dataToSend = { ...rest };
      // send image only if base64 (new)
      if (image && typeof image === "string" && image.startsWith("data:image/")) {
        dataToSend.image = image;
      }

      await fetchWithToken(
        `${process.env.REACT_APP_API_URL}/service-categories/update?page_id=${pageId}`,
        "PATCH",
        dataToSend
      );

      toast({
        title: t("Category updated successfully"),
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      await fetchServiceCategories(serviceId);
      setEditingCategory(null);
    } catch (error) {
      toast({
        title: t("Failed to update category"),
        description: error.message || t("Could not update category"),
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  // ---------- table desktop ----------
  const renderDesktopTable = () => {
    if (!filteredCategories || filteredCategories.length === 0) {
      return (
        <Box p="20px" textAlign="center">
          <Text fontSize="lg" color="gray.500">
            {t("No categories found")}
          </Text>
        </Box>
      );
    }

    return (
         <Box overflowX="auto" maxHeight="400px" px="20px">
      <Table variant="striped" colorScheme="gray" mb="24px" size="sm">
           <Thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                        <Tr bg='rgb(69, 50, 240)' textColor="white" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <Th color="white">{t("Image")}</Th>
              <Th color="white">{t("Name")}</Th>
              <Th color="white">{t("Translations")}</Th>
              <Th color="white">{t("Status")}</Th>
              <Th color="white">{t("Actions")}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredCategories.map((category) => (
              <Tr
                key={category.id}
                bg={editingCategory?.id === category.id ? "blue.50" : "transparent"}
                borderWidth={editingCategory?.id === category.id ? "2px" : "0"}
                borderColor={editingCategory?.id === category.id ? "blue.400" : "transparent"}
                _hover={{
                  bg: editingCategory?.id === category.id ? "blue.100" : "gray.50",
                }}
              >
                {/* image */}
                <Td>
                  {editingCategory?.id === category.id ? (
                    <Input
                      type="file"
                      accept="image/*"
                      size="sm"
                      onChange={handleCategoryImageUpload}
                    />
                  ) : category.image ? (
                    <Image
                      src={category.image}
                      alt={category.name}
                      boxSize="50px"
                      objectFit="cover"
                      borderRadius="md"
                    />
                  ) : (
                    <Text fontSize="sm" color="gray.400">
                      {t("No Image")}
                    </Text>
                  )}
                </Td>
                {/* name */}
                <Td>
                  {editingCategory?.id === category.id ? (
                    <Input
                      size="sm"
                      value={editingCategory.name}
                      onChange={(e) =>
                        setEditingCategory((prev) => ({ ...prev, name: e.target.value }))
                      }
                      focusBorderColor="blue.400"
                      borderColor="blue.300"
                    />
                  ) : (
                    <Text>{category.name}</Text>
                  )}
                </Td>
                {/* translations */}
                <Td minWidth="220px">
                  {editingCategory?.id === category.id ? (
                    <Flex direction="column" gap={2}>
                      <Flex align="center">
                        <Text fontWeight="bold" minWidth="60px" mr={2}>
                          Key:
                        </Text>
                        <Input
                          value={editingCategory.translation_key || ""}
                          isDisabled
                          size="sm"
                        />
                      </Flex>
                      <Flex align="center">
                        <Text fontWeight="bold" minWidth="60px" mr={2}>
                          FR:
                        </Text>
                        <Input
                          value={editingCategory.translations?.fr || ""}
                          onChange={(e) =>
                            setEditingCategory((prev) => ({
                              ...prev,
                              translations: {
                                ...prev.translations,
                                fr: e.target.value,
                              },
                            }))
                          }
                          size="sm"
                        />
                      </Flex>
                      <Flex align="center">
                        <Text fontWeight="bold" minWidth="60px" mr={2}>
                          IT:
                        </Text>
                        <Input
                          value={editingCategory.translations?.it || ""}
                          onChange={(e) =>
                            setEditingCategory((prev) => ({
                              ...prev,
                              translations: {
                                ...prev.translations,
                                it: e.target.value,
                              },
                            }))
                          }
                          size="sm"
                        />
                      </Flex>
                    </Flex>
                  ) : (
                    <Flex direction="column">
                      {category.translation_key && (
                        <Text fontSize="sm">Key: {category.translation_key}</Text>
                      )}
                      {category.translations?.fr && (
                        <Text fontSize="sm">FR: {category.translations.fr}</Text>
                      )}
                      {category.translations?.it && (
                        <Text fontSize="sm">IT: {category.translations.it}</Text>
                      )}
                      {!category.translation_key &&
                        !category.translations?.fr &&
                        !category.translations?.it && (
                          <Text fontSize="sm" color="gray.500">
                            {t("No translations")}
                          </Text>
                        )}
                    </Flex>
                  )}
                </Td>
                {/* status */}
               <Td>
  {editingCategory && editingCategory.id === category.id ? (
    <Flex align="center">
      <Switch
        isChecked={editingCategory?.status} // Use the boolean value directly
        onChange={(e) =>
          setEditingCategory((prev) => ({
            ...prev,
            status: e.target.checked, // Update the boolean value
          }))
        }
        colorScheme="cyan"
        mr={2}
      />
      <Badge
        colorScheme={editingCategory?.status ? "green" : "red"} // Check the boolean value
        variant="solid"
        px={2}
        py={1}
        borderRadius="md"
        fontSize="0.85em"
      >
        {editingCategory?.status ? t("Active") : t("Inactive")}
      </Badge>
    </Flex>
  ) : (
    <Badge
      colorScheme={category?.status ? "green" : "red"} // Check the boolean value
      variant="solid"
      px={2}
      py={1}
      borderRadius="md"
      fontSize="0.85em"
    >
      {category?.status ? t("Active") : t("Inactive")}
    </Badge>
  )}
</Td>
                {/* actions */}
                <Td>
                  {editingCategory?.id === category.id ? (
                    <Flex gap={2}>
                      <Button
                        colorScheme="green"
                        size="sm"
                        onClick={handleUpdateCategory}
                        leftIcon={<MdDone />}
                      >
                        {t("Save")}
                      </Button>
                      <Button
                        colorScheme="red"
                        size="sm"
                        onClick={() => setEditingCategory(null)}
                        leftIcon={<MdCancel />}
                      >
                        {t("Cancel")}
                      </Button>
                    </Flex>
                  ) : (
                    <Flex gap={2}>
                      <Button
                        colorScheme="cyan"
                        size="sm"
                        color="white"
                        leftIcon={<MdEdit />}
                        onClick={() =>
                          setEditingCategory({
                            id: category.id,
                            name: category.name,
                            translation_key: category.translation_key,
                            translations: category.translations || {},
                            image: category.image,
                            status: category.status,
                            service_id: category.service_id,
                          })
                        }
                        isDisabled={!permissions.canEdit}
                      >
                        {t("Edit")}
                      </Button>
                    </Flex>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    );
  };

  // -------- mobile cards ----------
  const renderMobileView = () => {
    if (!filteredCategories || filteredCategories.length === 0) {
      return (
        <Box p="20px" textAlign="center">
          <Text fontSize="lg" color="gray.500">
            {t("No categories found")}
          </Text>
        </Box>
      );
    }
    return (
      <SimpleGrid columns={{ base: 1 }} spacing={4} px="10px">
        {filteredCategories.map((category) => (
          <Card key={category.id} p="4" borderRadius="md" shadow="md">
            <Flex gap={3} align="center" mb={3}>
              {category.image ? (
                <Image src={category.image} alt={category.name} boxSize="50px" borderRadius="md" />
              ) : (
                <Box
                  boxSize="50px"
                  bg="gray.200"
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="xs"
                  color="gray.500"
                >
                  {t("No Image")}
                </Box>
              )}
              <Box>
                <Text fontWeight="bold">{category.name}</Text>
                <Text fontSize="xs" color="gray.500">
                  {category.translation_key}
                </Text>
              </Box>
            </Flex>
            <Flex justify="space-between" align="center">
              <Badge colorScheme={category.status ? "green" : "red"}>
                {category.status ? t("Active") : t("Inactive")}
              </Badge>
              <Button
                size="xs"
                colorScheme="cyan"
                color="white"
                leftIcon={<MdEdit />}
                onClick={() =>
                  setEditingCategory({
                    id: category.id,
                    name: category.name,
                    translation_key: category.translation_key,
                    translations: category.translations || {},
                    image: category.image,
                    status: category.status,
                    service_id: category.service_id,
                  })
                }
              >
                {t("Edit")}
              </Button>
            </Flex>
          </Card>
        ))}
      </SimpleGrid>
    );
  };

  return (
    <>
  <Card
        direction="column"
        w="100%"
        px="0px"
        overflowX={{ base: "scroll", lg: "hidden" }}
        boxShadow="lg"
      >    
          <Flex
          px={{ base: "10px", md: "25px" }}
          justify="space-between"
          mb="20px"
          align="center"
          flexWrap="wrap"
        >
          <Flex align="center" gap={2}>
            <Text fontSize={{ base: "18px", md: "22px" }} fontWeight="700">
              {t("Service Categories for")} {serviceName ? `"${serviceName}"` : ""}
            </Text>
            <Button
              colorScheme="cyan"
              size="sm"
              color="white"
              leftIcon={<MdAdd />}
              onClick={onOpen}
              isDisabled={!permissions.canInsert}
            >
              {t("Add Category")}
            </Button>
          </Flex>
          <Input
            placeholder={t("Search categories...")}
            value={searchQuery}
            onChange={handleSearch}
            size="sm"
            w={{ base: "100%", md: "200px" }}
            mt={{ base: 2, md: 0 }}
          />
        </Flex>

        {loading ? (
          <Box p="20px" textAlign="center">
            <Text>{t("Loading categories...")}</Text>
          </Box>
        ) : isMobile ? (
          renderMobileView()
        ) : (
          renderDesktopTable()
        )}
      </Card>

      {/* Add Category Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Add Category")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <InsertServiceCategory
              serviceId={serviceId}
              onSuccess={() => {
                onClose();
                fetchServiceCategories(serviceId);
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ListServiceCategories;
